import styled, { css } from 'styled-components';
import {
    ANIMATION_CURVE,
    ANIMATION_DURATION,
    getSpacingToken,
    getTextTokenPreset,
    getToken,
    Size,
} from '@optimus-web/common';
import { PlaceholderElement } from '../Input';

interface TextareaStyledComponentProps {
    inputSize?: Size;
    showScrollbar?: boolean;
    autosize?: boolean;
    disabled?: boolean;
}

const getPaddingForSizeVariant = ({ inputSize }: { inputSize?: Size }) => {
    const basePadding = getSpacingToken('100');

    switch (inputSize) {
        case 'large':
        case 'extraLarge':
            return css`
                padding: ${basePadding}px ${getSpacingToken('200')}px;
            `;
        case 'medium':
            return css`
                padding: ${basePadding}px ${getSpacingToken('150')}px;
            `;
        case 'small':
        default:
            return css`
                padding: ${basePadding}px;
            `;
    }
};

export const TextareaStyledComponent = styled.textarea<TextareaStyledComponentProps>`
    background: transparent;
    border: ${getToken('borderWidth0')};
    ${getTextTokenPreset('bodyMedium')};
    color: ${({ disabled }) => (disabled ? getToken('textDisabled') : getToken('textStaticPrimary'))};
    outline: none;
    ${getPaddingForSizeVariant};
    resize: vertical;
    width: 100%;
    box-sizing: border-box;

    ${({ autosize }) =>
        autosize &&
        css`
            transition: height ${ANIMATION_DURATION.fast}ms ${ANIMATION_CURVE.default};
            resize: none;
            min-height: ${getToken('sizing500')}px;
        `}

    ${({ showScrollbar }) =>
        showScrollbar &&
        css`
            overflow-y: auto;
            padding-right: ${getSpacingToken('100')}px;
        `}
`;

export const TextareaPlaceholder = styled(PlaceholderElement)<{ inputSize?: Size }>`
    width: calc(
        100% - ${({ inputSize }) => (inputSize === 'small' ? getSpacingToken('100') : getSpacingToken('200'))}px
    );
    height: 100%;
    top: ${getSpacingToken('0')};
    left: ${({ inputSize }) => {
        switch (inputSize) {
            case 'small':
                return getSpacingToken('100');
            case 'medium':
                return getSpacingToken('150');
            case 'large':
            case 'extraLarge':
                return getSpacingToken('200');
            default:
                return getSpacingToken('150');
        }
    }}px;
    padding: ${getSpacingToken('100')}px ${getSpacingToken('0')};
    white-space: break-spaces;
    text-overflow: inherit;
    transform: translateY(${getSpacingToken('0')});
    word-break: break-word;
`;

export const ValueContainerElement = styled.div`
    position: relative;
    width: 100%;
    display: flex;
`;

export const TextareaContainerElement = styled.div`
    position: relative;
    display: flex;
    align-items: center;
    width: 100%;
`;

export const ShadowElement = styled.div`
    position: absolute;
    bottom: ${getSpacingToken('0')};
    left: ${getSpacingToken('50')}px;
    right: ${getSpacingToken('50')}px;
    height: ${getSpacingToken('300')}px;
    background: linear-gradient(transparent, ${getToken('backgroundStaticFlat')} ${getSpacingToken('250')}px);
    pointer-events: none;
`;
